import { cn } from '@atlas/ui-primitives'
import type { Extensions } from '@tiptap/core'
import Image from '@tiptap/extension-image'
import Link from '@tiptap/extension-link'
import { Placeholder } from '@tiptap/extensions'
import type { Editor } from '@tiptap/react'
import { EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { Bold, Heading1, Heading2, Italic, Link as LinkIcon, List, ListOrdered } from 'lucide-react'
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'
import { ARTICLE_PROSE_CLASS } from '@/lib/article-typography'
import { imageDropRule } from './rich-text-image-drop'

export interface RichTextEditorProps {
	/** Current value as HTML. */
	value: string
	/** Fires with the editor's HTML on every change ('<p></p>' → treated as empty by callers). */
	onChange: (html: string) => void
	placeholder?: string
	'aria-label'?: string
	/**
	 * Extra Tiptap extensions, appended to the shared set.
	 *
	 * The seam that keeps a feature's own node out of the shared editor: the article
	 * palette is registered by the composer, so a work-order description cannot
	 * contain one. Must be referentially stable — a fresh array each render would
	 * rebuild the editor and lose the cursor.
	 */
	extensions?: Extensions
	/** Extra toolbar controls, rendered after the shared ones and given the editor. */
	renderToolbarExtra?: (editor: Editor) => React.ReactNode
	/**
	 * Drop the editor's own border and toolbar frame so it reads as a page-level
	 * writing canvas rather than as a form field. The toolbar stays, separated by a
	 * rule instead of a box.
	 */
	variant?: 'field' | 'canvas'
	/**
	 * Take over a drop of image files, with the document position they landed on.
	 *
	 * The editor only reports the drop — it neither inserts nor uploads anything,
	 * because what a placement should look like before its bytes exist is the
	 * caller's decision. Absent, a dropped file is left to the browser as before.
	 */
	onDropFiles?: (files: File[], position: number) => void
}

/**
 * Imperative operations a caller cannot express through `value`: addressing a
 * POSITION in the document, which a controlled HTML string has no way to name.
 *
 * The three image operations are what an upload placed in the background needs —
 * show it, then either give it its stored URL or take it back out — and each is
 * addressed by `src` because that is the only handle a caller holds on a node it
 * did not create.
 */
export interface RichTextEditorHandle {
	/** Insert an image at `position`, or at the cursor when none is given. */
	insertImage: (image: { src: string; alt: string }, position?: number) => void
	/** Repoint the image carrying `from` at `to`. A no-op if it is no longer in the document. */
	replaceImageSrc: (from: string, to: string) => void
	/** Delete the image carrying `src`. A no-op if it is no longer in the document. */
	removeImage: (src: string) => void
}

/**
 * Where the image carrying `src` sits, with what it needs to be rewritten or cut.
 *
 * `null` is an ordinary outcome, not a failure: an author is free to delete a
 * placement while its upload is still running.
 */
function findImage(
	editor: Editor | null,
	src: string,
): { pos: number; size: number; attrs: Record<string, unknown> } | null {
	if (!editor) return null
	let found: { pos: number; size: number; attrs: Record<string, unknown> } | null = null
	editor.state.doc.descendants((node, pos) => {
		if (found) return false
		if (node.type.name === 'image' && node.attrs.src === src) {
			found = { pos, size: node.nodeSize, attrs: node.attrs }
		}
		return !found
	})
	return found
}

/** A single toolbar toggle button. */
function ToolBtn({
	active,
	label,
	onClick,
	children,
}: {
	active?: boolean
	label: string
	onClick: () => void
	children: React.ReactNode
}) {
	return (
		<button
			type="button"
			aria-label={label}
			aria-pressed={active}
			onMouseDown={(e) => e.preventDefault()}
			onClick={onClick}
			className={cn(
				'flex h-7 w-7 items-center justify-center rounded-ds-sm text-ds-text-muted hover:bg-ds-surface hover:text-ds-text',
				active && 'bg-ds-surface text-ds-text',
			)}
		>
			{children}
		</button>
	)
}

const ICON_CLASS = 'h-3.5 w-3.5'

function EditorToolbar({
	editor,
	renderExtra,
	className,
	withHeadings,
}: {
	editor: Editor
	renderExtra?: (editor: Editor) => React.ReactNode
	className: string
	withHeadings: boolean
}) {
	const toggleLink = () => {
		if (editor.isActive('link')) return editor.chain().focus().unsetLink().run()
		const url = globalThis.prompt('Link URL')
		if (url) editor.chain().focus().setLink({ href: url }).run()
	}

	return (
		<div className={className}>
			{/*
			 * Headings are offered only where they survive rendering. The `field` variant
			 * feeds short descriptions rendered through `SafeHtml`, whose allowlist drops
			 * every heading — offering the control there would let an author apply a
			 * heading that silently vanishes on the page that displays it.
			 */}
			{withHeadings ? (
				<>
					<ToolBtn
						label="Heading 1"
						active={editor.isActive('heading', { level: 1 })}
						onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
					>
						<Heading1 className={ICON_CLASS} />
					</ToolBtn>
					<ToolBtn
						label="Heading 2"
						active={editor.isActive('heading', { level: 2 })}
						onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
					>
						<Heading2 className={ICON_CLASS} />
					</ToolBtn>
					<span aria-hidden className="mx-0.5 h-4 w-px bg-ds-border" />
				</>
			) : null}
			<ToolBtn label="Bold" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}>
				<Bold className={ICON_CLASS} />
			</ToolBtn>
			<ToolBtn
				label="Italic"
				active={editor.isActive('italic')}
				onClick={() => editor.chain().focus().toggleItalic().run()}
			>
				<Italic className={ICON_CLASS} />
			</ToolBtn>
			<ToolBtn
				label="Bullet list"
				active={editor.isActive('bulletList')}
				onClick={() => editor.chain().focus().toggleBulletList().run()}
			>
				<List className={ICON_CLASS} />
			</ToolBtn>
			<ToolBtn
				label="Numbered list"
				active={editor.isActive('orderedList')}
				onClick={() => editor.chain().focus().toggleOrderedList().run()}
			>
				<ListOrdered className={ICON_CLASS} />
			</ToolBtn>
			<ToolBtn label="Link" active={editor.isActive('link')} onClick={toggleLink}>
				<LinkIcon className={ICON_CLASS} />
			</ToolBtn>
			{renderExtra?.(editor)}
		</div>
	)
}

const FRAME_CLASS: Record<NonNullable<RichTextEditorProps['variant']>, string> = {
	field: 'rounded-ds-md border border-ds-border bg-ds-background focus-within:ring-2 focus-within:ring-ds-ring',
	canvas: '',
}

const TOOLBAR_CLASS: Record<NonNullable<RichTextEditorProps['variant']>, string> = {
	field: 'flex items-center gap-0.5 border-b border-ds-border px-1.5 py-1',
	// A rule above the toolbar rather than a box around everything: the controls
	// stay reachable without drawing a form field around the document.
	canvas:
		'sticky top-0 z-10 -mx-1 flex items-center gap-0.5 border-b border-ds-border bg-ds-background/95 px-1 py-1.5 backdrop-blur',
}

const CONTENT_CLASS: Record<NonNullable<RichTextEditorProps['variant']>, string> = {
	field: 'min-h-28 px-3 py-2 text-ds-base',
	canvas: 'min-h-96 py-4 text-ds-lg',
}

/**
 * The placeholder, drawn INSIDE the empty paragraph the caret is in rather than as
 * an overlay positioned over it.
 *
 * That is what keeps the prompt on the same line as the cursor: an overlay has to
 * restate the content area's padding and leading, and it drifts the moment the
 * variant's prose adds anything of its own - the canvas paragraph carries `my-3`
 * and `leading-relaxed`, so the caret sat a line's margin below the text the author
 * was reading. Here there is nothing to keep in sync, because the placeholder
 * occupies the paragraph's own box (`float` + `h-0` so it takes no space).
 *
 * Scoped to `is-editor-empty`, which the extension applies only while the WHOLE
 * document is empty - an empty paragraph the author lands on mid-document carries
 * `is-empty` instead and is left alone.
 */
const PLACEHOLDER_CLASS = [
	'[&_.is-editor-empty]:before:content-[attr(data-placeholder)]',
	'[&_.is-editor-empty]:before:float-left',
	'[&_.is-editor-empty]:before:h-0',
	'[&_.is-editor-empty]:before:text-ds-text-subtle',
	'[&_.is-editor-empty]:before:pointer-events-none',
].join(' ')

/**
 * A small WYSIWYG editor (Tiptap) producing HTML — headings, bold, italic,
 * bullet/ordered lists, links, and images placed through the imperative handle.
 * Controlled on `value` (HTML). The stored HTML must be sanitized before rendering
 * elsewhere (`SafeHtml` for short descriptions, `SafeArticleHtml` for an article);
 * Tiptap's own output is limited to the enabled marks/nodes, but never trust it on
 * render.
 *
 * Heading levels are capped at the three both sanitizer profiles can admit, so a
 * pasted `<h4>` lands as a paragraph rather than as a heading that renders as
 * nothing. `SafeHtml`'s allowlist is unchanged and still drops every heading, so a
 * heading typed into a work-order description is stripped on render as before.
 */
export const RichTextEditor = forwardRef<RichTextEditorHandle, RichTextEditorProps>(function RichTextEditor(
	{
		value,
		onChange,
		placeholder,
		'aria-label': ariaLabel,
		extensions = [],
		renderToolbarExtra,
		variant = 'field',
		onDropFiles,
	},
	ref,
) {
	// Read through a ref so the drop handler ProseMirror holds from creation always
	// calls the current one, without `onDropFiles` having to be referentially stable
	// the way `extensions` must be.
	const dropHandler = useRef(onDropFiles)
	useEffect(() => {
		dropHandler.current = onDropFiles
	}, [onDropFiles])

	const editor = useEditor({
		extensions: [
			StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
			Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { rel: 'noopener noreferrer' } }),
			// `inline: true` so a placed image joins the paragraph the cursor is in
			// instead of splitting it: the extension's default is a block node, which
			// made every placement its own top-level element and left an image
			// impossible to put in a line of text.
			//
			// `src` and `alt` only: the article sanitizer admits no other image
			// attribute, and it pins `src` to the public asset origin on render.
			Image.configure({ inline: true, allowBase64: false }),
			// Registered only when there is something to say, so an editor without a
			// placeholder gets no decoration at all.
			...(placeholder ? [Placeholder.configure({ placeholder })] : []),
			...extensions,
		],
		content: value,
		editorProps: {
			handleDrop: (view, event, _slice, moved) => imageDropRule(dropHandler.current)(view, event, moved),
			attributes: {
				role: 'textbox',
				'aria-multiline': 'true',
				'aria-label': ariaLabel ?? 'Description',
				class: cn(
					'max-w-none text-ds-text focus:outline-none',
					PLACEHOLDER_CLASS,
					// The article canvas is styled with the SAME rules the read-only render
					// uses, which is what makes "the reader sees what the author produced"
					// true rather than aspirational.
					variant === 'canvas'
						? ARTICLE_PROSE_CLASS
						: '[&_a]:text-ds-primary [&_a]:underline [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5',
					CONTENT_CLASS[variant],
				),
			},
		},
		onUpdate: ({ editor: e }) => onChange(e.getHTML()),
	})

	useImperativeHandle(
		ref,
		() => ({
			insertImage: ({ src, alt }, position) => {
				if (position === undefined) editor?.chain().focus().setImage({ src, alt }).run()
				// No `focus()`: a drop must not move the caret to where the file landed,
				// which would take the author out of the sentence they were writing.
				else editor?.chain().insertContentAt(position, { type: 'image', attrs: { src, alt } }).run()
			},
			replaceImageSrc: (from, to) => {
				const found = findImage(editor, from)
				if (!editor || !found) return
				editor.view.dispatch(editor.state.tr.setNodeMarkup(found.pos, undefined, { ...found.attrs, src: to }))
			},
			removeImage: (src) => {
				const found = findImage(editor, src)
				if (!editor || !found) return
				editor.view.dispatch(editor.state.tr.delete(found.pos, found.pos + found.size))
			},
		}),
		[editor],
	)

	// Keep the editor in sync when the value is reset/hydrated externally (edit mode),
	// without clobbering the cursor during normal typing.
	useEffect(() => {
		if (editor && value !== editor.getHTML()) editor.commands.setContent(value, { emitUpdate: false })
	}, [editor, value])

	if (!editor) return null

	return (
		<div className={FRAME_CLASS[variant]}>
			<EditorToolbar
				editor={editor}
				renderExtra={renderToolbarExtra}
				className={TOOLBAR_CLASS[variant]}
				withHeadings={variant === 'canvas'}
			/>
			{/* Emptiness is decided from the editor's own document, not from a React
			    render of `value`: the decoration is rebuilt from ProseMirror state on
			    every transaction, so a body written from outside - applying the brand
			    template, which the sync effect below writes with `emitUpdate: false` and
			    therefore schedules no render for - clears the placeholder immediately
			    rather than leaving it sitting on top of the template. */}
			<EditorContent editor={editor} />
		</div>
	)
})
